import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AthleteSelect } from "@/components/AthleteSelect";
import UncoveredAthletesAlert from "@/components/UncoveredAthletesAlert";
import { useSearchParams } from "react-router-dom";

const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; };
const today = () => new Date().toISOString().slice(0, 10);
const SOURCE = { lezione: "Lezione", lezione_rapida: "Lezione rapida", attivita_scherma: "Attività scherma" };

export default function LessonsArchive({ athletes, groups, coaches }) {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ from: sp.get("athlete") ? new Date(Date.now() - 365 * 864e5).toISOString().slice(0, 10) : monthStart(), to: today(), coach_id: "", athlete_id: sp.get("athlete") || "", group_id: "" });
  const [data, setData] = useState(null);
  const [view, setView] = useState("coach"); // coach | athlete | list

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/lessons-archive", { params }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [f]);

  const pairsByCoach = useMemo(() => {
    const m = {};
    (data?.pairs || []).forEach((p) => { (m[p.coach_id || "__none"] ||= []).push(p); });
    return m;
  }, [data]);

  return (
    <div className="space-y-5" data-testid="lessons-archive">
      <UncoveredAthletesAlert days={30} groupId={f.group_id} compact />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div><Label className="text-xs">Dal</Label><Input data-testid="la-from" type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} /></div>
        <div><Label className="text-xs">Al</Label><Input data-testid="la-to" type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} /></div>
        <div>
          <Label className="text-xs">Maestro</Label>
          <Select value={f.coach_id || "all"} onValueChange={(v) => setF({ ...f, coach_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="la-coach"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem>{coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Gruppo</Label>
          <Select value={f.group_id || "all"} onValueChange={(v) => setF({ ...f, group_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="la-group"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti</SelectItem>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Atleta</Label><AthleteSelect value={f.athlete_id} onChange={(v) => setF({ ...f, athlete_id: v })} athletes={athletes} groups={groups} testid="la-athlete" placeholder="Tutti" allowClear /></div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Lezioni" value={data.total} testId="la-total" />
            <Kpi label="Minuti totali" value={data.minutes} hint={data.total ? `${Math.round(data.minutes / data.total)}′ medi` : ""} />
            <Kpi label="Atleti seguiti" value={data.athletes_covered} />
            <Kpi label="Maestri attivi" value={data.coaches_active} />
            <Kpi label="Senza maestro" value={data.without_coach} tone={data.without_coach ? "text-amber-700" : ""} />
          </div>

          <div className="flex items-center border border-zinc-200 rounded-md overflow-hidden w-fit">
            {[["coach", "Per maestro"], ["athlete", "Per atleta"], ["list", "Elenco"]].map(([k, l]) => (
              <button key={k} data-testid={`la-view-${k}`} onClick={() => setView(k)} className={`px-3 py-2 text-sm ${view === k ? "bg-black text-white" : "hover:bg-zinc-50"}`}>{l}</button>
            ))}
          </div>

          {view === "coach" && (
            <div className="space-y-3">
              {data.by_coach.length === 0 && <Empty />}
              {data.by_coach.map((c) => (
                <div key={c.coach_id || "none"} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`la-coach-row-${c.coach_id || "none"}`}>
                  <div className="flex flex-wrap items-baseline gap-4">
                    <div className="font-heading font-semibold text-lg">{c.name}</div>
                    <div className="font-mono text-sm"><span className="text-2xl font-bold">{c.lessons}</span> lezioni</div>
                    <div className="text-sm text-zinc-500 font-mono">{c.minutes}′ · {c.avg_duration}′ medi · {c.distinct} atleti · ultima {c.last_date}</div>
                    {c.avg_evaluation != null && <div className="text-sm text-zinc-500">valutazione media {c.avg_evaluation}/5</div>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(pairsByCoach[c.coach_id || "__none"] || []).map((p) => (
                      <span key={p.athlete_id} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">{p.athlete_name} <span className="font-mono font-semibold">{p.lessons}</span></span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {view === "athlete" && (
            <Table cols={["Atleta", "Gruppo", "Lezioni", "Minuti", "Maestri", "Ultima", "Val. media"]}>
              {data.by_athlete.length === 0 && <tr><td colSpan={7}><Empty /></td></tr>}
              {data.by_athlete.map((a) => {
                const g = data.rows.find((r) => r.athlete_id === a.athlete_id)?.group_name;
                return (
                  <tr key={a.athlete_id || "none"} className="border-t border-zinc-100" data-testid={`la-athlete-row-${a.athlete_id}`}>
                    <td className="px-4 py-2 font-medium">{a.name}</td>
                    <td className="px-4 py-2 text-zinc-500 text-xs">{g || "—"}</td>
                    <td className="px-4 py-2 font-mono font-bold text-right">{a.lessons}</td>
                    <td className="px-4 py-2 font-mono text-right">{a.minutes}′</td>
                    <td className="px-4 py-2 font-mono text-right">{a.distinct}</td>
                    <td className="px-4 py-2 font-mono text-xs">{a.last_date}</td>
                    <td className="px-4 py-2 font-mono text-right">{a.avg_evaluation ?? "—"}</td>
                  </tr>
                );
              })}
            </Table>
          )}

          {view === "list" && (
            <Table cols={["Data", "Atleta", "Maestro", "Durata", "Obiettivo", "Fonte"]}>
              {data.rows.length === 0 && <tr><td colSpan={6}><Empty /></td></tr>}
              {data.rows.map((r, i) => (
                <tr key={`${r.id}-${i}`} className="border-t border-zinc-100" data-testid={`la-row-${r.id}`}>
                  <td className="px-4 py-2 font-mono text-xs">{r.date}</td>
                  <td className="px-4 py-2 font-medium">{r.athlete_name}</td>
                  <td className="px-4 py-2">{r.coach_name}</td>
                  <td className="px-4 py-2 font-mono text-right">{r.duration ? `${r.duration}′` : "—"}</td>
                  <td className="px-4 py-2 text-xs text-zinc-600 truncate max-w-xs">{r.objective || "—"}</td>
                  <td className="px-4 py-2 text-[10px] uppercase tracking-wider text-zinc-500">{SOURCE[r.source]}</td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, tone = "", testId }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-heading text-2xl font-bold ${tone}`} data-testid={testId}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-400 font-mono">{hint}</div>}
    </div>
  );
}
function Table({ cols, children }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50"><tr>{cols.map((c) => <th key={c} className="text-left px-4 py-2">{c}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
function Empty() { return <div className="text-sm text-zinc-500 py-6 text-center">Nessuna lezione nel periodo con i filtri selezionati.</div>; }
